import React from 'react';
import { useCredits } from '../../../hooks/useCredits';
import { RocketIcon, DiamondIcon, CrownIcon, CreditCardIcon } from '../Icons';
import './CreditBalance.scss';

const CreditBalance = ({ onClick }) => {
  const { balance, subscriptionTier, loading } = useCredits();

  // Use cached value immediately if available, to prevent animation jump
  const [displayBalance, setDisplayBalance] = React.useState(() => {
    try {
      const cached = localStorage.getItem('creditBalance');
      return cached && cached !== 'null' ? parseInt(cached, 10) : null;
    } catch (error) {
      return null;
    }
  });

  // Animation states
  const [isUpdating, setIsUpdating] = React.useState(false);
  const [prevBalance, setPrevBalance] = React.useState(null); // Start with null to ensure first real balance triggers comparison

  // Update prevBalance when component first gets a real balance
  React.useEffect(() => {
    if (prevBalance === null && balance !== null) {
      setPrevBalance(balance);
    }
  }, [balance, prevBalance]);

  // Detect balance changes and trigger animation
  React.useEffect(() => {
    if (balance !== null && balance !== undefined) {
      // If this is the first time we're getting a real balance, just set it without animation
      if (prevBalance === null) {
        setDisplayBalance(balance);
        setPrevBalance(balance);
        return;
      }
      // If balance actually changed, trigger animation
      if (balance !== prevBalance) {
        setIsUpdating(true);
        // Clear animation after duration
        setTimeout(() => {
          setIsUpdating(false);
        }, 2000);
        setDisplayBalance(balance);
        setPrevBalance(balance);
      }
    }
  }, [balance, prevBalance]); // Add console log to track isUpdating state
  React.useEffect(() => {
    // Tracking isUpdating state changes
  }, [isUpdating]); // Test function to manually trigger animation (for debugging)
  const testAnimation = () => {
    setIsUpdating(true);
    setTimeout(() => {
      setIsUpdating(false);
    }, 2000);
  };
  // Show loading only if we don't have any balance to display
  if ((loading || balance === null) && displayBalance === null) {
    return (
      <div className='credit-balance loading'>
        <div className='credit-skeleton' />
      </div>
    );
  }
  const getTierColor = tier => {
    switch (tier) {
      case 'FREE':
        return '#0C0C0B';
      case 'STARTER':
        return '#FF411A';
      case 'PRO':
        return '#FF411A';
      case 'PREMIUM':
        return '#FF411A';
      default:
        return '#0C0C0B';
    }
  };
  const getTierIcon = tier => {
    switch (tier) {
      case 'FREE':
        return <CreditCardIcon />;
      case 'STARTER':
        return <RocketIcon />;
      case 'PRO':
        return <DiamondIcon />;
      case 'PREMIUM':
        return <CrownIcon />;
      default:
        return <CreditCardIcon />;
    }
  };
  return (
    <div
      className='credit-balance'
      style={{
        transform: isUpdating ? 'scale(1.05)' : 'scale(1)',
        boxShadow: isUpdating
          ? '0 1px 6px rgba(0, 0, 0, 0.08), 0 0 25px rgba(255, 65, 26, 0.6)'
          : '0 1px 6px rgba(0, 0, 0, 0.08)',
        backgroundColor: isUpdating ? 'rgba(255, 65, 26, 0.1)' : 'rgba(130, 49, 49, 0)',
        borderColor: isUpdating ? 'rgba(255, 65, 26, 0.3)' : 'rgba(255, 255, 255, 0.1)',
        transition: 'all 0.5s ease-in-out',
      }}
      onClick={onClick}
      role='button'
      tabIndex={0}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          onClick();
        }
      }}
      onDoubleClick={testAnimation} // Double-click to test animation
    >
      <div className='credit-info'>
        <div className='credit-details'>
          <span
            className='credit-amount'
            style={{
              transform: isUpdating ? 'scale(1.2)' : 'scale(1)',
              color: isUpdating ? '#FF411A' : '#1f2937',
              fontWeight: isUpdating ? '700' : '600',
              transition: 'all 0.3s ease-in-out',
            }}
          >
            {displayBalance}
          </span>
          <span className='credit-label'>credits</span>
        </div>
      </div>
      <div className='subscription-tier' style={{ color: getTierColor(subscriptionTier) }}>
        <span className='tier-icon'>{getTierIcon(subscriptionTier)}</span>
        <span className='tier-name'>{subscriptionTier}</span>
      </div>
    </div>
  );
};
export default CreditBalance;
